// RecoverOS Connect — frontend helpers.
// Milli-credits are an INTERNAL representation. Customers only ever see RC.
export const MILLI_PER_RC = 1000;

// Format integer milli-credits as a human RC string (e.g. 250 -> "0.25 RC", 1000 -> "1 RC").
export function rc(milli) {
  const v = (Number(milli || 0) / MILLI_PER_RC);
  const s = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
  return `${s} RC`;
}

// Channel presentation for Connect (WhatsApp • Email • SMS • Voice).
export const CONNECT_CHANNELS = ["whatsapp", "email", "sms", "voice"];

// Friendly labels for the tenant_channels lifecycle states (Phase 1A statuses).
export const CHANNEL_STATUS_LABEL = {
  not_connected: "Not Connected",
  setup_required: "Setup Required",
  setup_started: "Setup Started",
  pending_approval: "Pending Meta Approval",
  connected: "Connected",
  restricted: "Restricted",
  disconnected: "Disconnected",
  error: "Error",
};

// Visual tone per status.
export const CHANNEL_STATUS_TONE = {
  not_connected: "bg-slate-100 text-slate-600 border-slate-200",
  setup_required: "bg-amber-100 text-amber-800 border-amber-200",
  setup_started: "bg-blue-100 text-blue-800 border-blue-200",
  pending_approval: "bg-violet-100 text-violet-800 border-violet-200",
  connected: "bg-emerald-100 text-emerald-800 border-emerald-200",
  restricted: "bg-orange-100 text-orange-800 border-orange-200",
  disconnected: "bg-slate-100 text-slate-600 border-slate-200",
  error: "bg-red-100 text-red-800 border-red-200",
};

// IMPORTANT (Phase 1B): NO channel is production-enabled yet. Real sending arrives channel-by-channel
// (Email = Phase 1C, SMS = Phase 2, WhatsApp = Phase 3, Voice = Phase 4). Until then the composer must
// NEVER call the legacy mocked send endpoint or present a simulated send as a real one.
export const CHANNEL_READY = {
  whatsapp: false,
  email: false, // becomes true only when Phase 1C managed email is live for tenant messaging
  sms: false,
  voice: false,
};

// The message shown when a channel is selected in the composer but cannot actually send.
export const CHANNEL_UNAVAILABLE_MSG = {
  whatsapp: "Connect WhatsApp to use this channel.",
  email: "Email setup required.",
  sms: "SMS activation required.",
  voice: "Voice will be available soon.",
};

// Status-driven primary CTA for a channel card.
export function channelCta(channel, status) {
  if (channel === "voice") return "Coming Soon";
  if (channel === "sms") return "Set Up SMS";
  if (channel === "email") return status === "connected" ? "Send Email" : "Set Up Email";
  // whatsapp
  switch (status) {
    case "connected": return "Manage";
    case "setup_started": return "Continue Setup";
    case "pending_approval": return "View Status";
    case "restricted":
    case "error": return "View Status";
    default: return "Connect WhatsApp";
  }
}

// Concise, customer-facing description per channel (shown on the primary card).
export const CHANNEL_DESC = {
  whatsapp: "WhatsApp availability is subject to Meta approval, eligibility and applicable requirements.",
  email: "Send payment reminders and follow-ups by email.",
  sms: "Send payment reminders and follow-ups by SMS.",
  voice: "Automated voice reminders and follow-up calls.",
};

// Detailed setup requirements — revealed ONLY after the user clicks the card's CTA
// (never shown on the primary card, to keep it clean and non-technical).
export const CHANNEL_SETUP_REQUIREMENTS = {
  email: "Managed customer email is rolling out for RecoverOS Connect. You'll set a verified sender and reply-to address for your workspace, then send payment reminders and follow-ups by email. We'll enable it for your workspace shortly — no messages are ever sent until it's live.",
  sms: "SMS requires an operator connection and DLT (Distributed Ledger Technology) registration of your business entity, sender IDs and message templates as per TRAI regulations. Once activated you can send payment reminders and follow-ups by SMS.",
  whatsapp: "WhatsApp Business messaging uses Meta's WhatsApp Cloud API and is subject to Meta approval, business verification, eligibility and applicable requirements. No connection is simulated — your account is only ever activated after real approval.",
};

// Distinct status presentation (dot colour) — clear, not cluttered.
export const CHANNEL_STATUS_DOT = {
  connected: "bg-emerald-500",
  not_connected: "bg-slate-300",
  setup_required: "bg-amber-500",
  setup_started: "bg-blue-500",
  pending_approval: "bg-violet-500",
  restricted: "bg-orange-500",
  disconnected: "bg-slate-300",
  error: "bg-red-500",
  coming_soon: "bg-slate-300",
};

// Resolve the label + status-key a card should display. Email/SMS always read "Setup Required"
// (they need real activation, not a one-click connect); Voice reads "Coming Soon"; WhatsApp
// reflects its true tenant_channels lifecycle state.
export function channelStatusDisplay(channel, status) {
  if (channel === "voice") return { key: "coming_soon", label: "Coming Soon" };
  if (channel === "email" || channel === "sms") {
    if (status === "connected") return { key: "connected", label: "Connected" };
    return { key: "setup_required", label: "Setup Required" };
  }
  return { key: status || "not_connected", label: CHANNEL_STATUS_LABEL[status] || "Not Connected" };
}
